/**
 * InviteLinkPage - Desktop full page for generating/sharing the invite link
 * Route: /trips/:id/invite
 * Design: tripways.pen node x5Vft
 *
 * Mobile users reach the same content via InviteLinkSheet (bottom sheet),
 * not this page — see TripMenuSheet.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Compass, Link as LinkIcon, MessageCircle, Mail, MessageSquare } from 'lucide-react';
import DesktopSidebar from '../components/common/DesktopSidebar';
import InviteLinkCard from '../components/members/InviteLinkCard';
import { useTrips } from '../contexts/TripContext';

export default function InviteLinkPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { trips } = useTrips();
  const trip = trips.find((t) => t.id === id);

  if (!id) return null;

  const shareUrl = `${window.location.origin}/join/${id}`;

  const handleShareLine = () => {
    window.open(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const handleShareEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(
      `邀請你加入「${trip?.name ?? '旅程'}」`
    )}&body=${encodeURIComponent(`一起規劃這趟旅程吧！點擊連結加入：${shareUrl}`)}`;
  };

  const handleShareMessenger = () => {
    // Messenger 網頁分享需要 Facebook App ID，目前尚未申請，先保留樣式不接功能
  };

  return (
    <div className="flex min-h-screen bg-[#fafafa]">
      <DesktopSidebar onNavigate={navigate} tripId={id} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* ─── Mobile Header ───
            桌面版由 DesktopSidebar/TripMenuSheet 導覽至此頁；行動版設計稿走 InviteLinkSheet
            bottom sheet（見 TripMenuSheet），但這個路由本身仍可能被直接開啟（分享連結、瀏覽器前進/
            後退），此時若無此 header 會完全沒有返回入口。 */}
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-100">
          <div className="flex items-center gap-1 px-5 py-4">
            <button onClick={() => navigate(-1)} className="flex items-center justify-center w-11 h-11 -ml-2">
              <ArrowLeft size={22} className="text-slate-900" />
            </button>
            <h1 className="text-slate-900 text-[17px] font-bold">邀請成員</h1>
          </div>
        </header>

        <header className="hidden lg:flex items-center justify-between bg-white border-b border-slate-200 px-10 py-5">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 rounded-[10px] border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-900"
            >
              <ArrowLeft size={16} />
              返回
            </button>
            <h1 className="text-slate-900 text-xl font-bold">邀請成員</h1>
            {trip && (
              <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3.5 py-1.5 text-xs font-medium text-slate-500">
                <Compass size={13} />
                {trip.name}
              </span>
            )}
          </div>
        </header>

        <main className="flex-1 flex flex-col items-center p-10">
          <div className="w-full max-w-[560px] rounded-2xl bg-white shadow-sm p-10 flex flex-col gap-7">
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="w-14 h-14 rounded-full bg-[#0dccf210] flex items-center justify-center">
                <LinkIcon size={24} className="text-primary" />
              </div>
              <h2 className="text-slate-900 text-xl font-bold">邀請連結</h2>
              <p className="text-sm text-slate-500">分享此連結，讓其他人加入你的旅程</p>
            </div>

            <InviteLinkCard tripId={id} />

            <div className="flex justify-center gap-3">
              <ShareButton icon={MessageCircle} label="LINE" color="#06C755" onClick={handleShareLine} />
              <ShareButton icon={Mail} label="Email" color="#555555" onClick={handleShareEmail} />
              <ShareButton
                icon={MessageSquare}
                label="Messenger"
                color="#0084FF"
                onClick={handleShareMessenger}
                disabled
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function ShareButton({
  icon: Icon,
  label,
  color,
  onClick,
  disabled,
}: {
  icon: typeof MessageCircle;
  label: string;
  color: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center gap-1.5 w-[72px] disabled:opacity-40"
    >
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon size={20} style={{ color }} />
      </div>
      <span className="text-[11px] font-medium text-slate-500">{label}</span>
    </button>
  );
}
